<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps({
  section: { type: String, default: '' },
  number: { type: String, default: '' },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  meta: { type: String, default: '' }
})

const route = useRoute()

const heroMeta = computed(() => route.meta?.hero ?? {})

const displaySection = computed(() => props.section || heroMeta.value.section || '')
const displayNumber = computed(() => props.number || heroMeta.value.number || '')
const displayTitle = computed(() => props.title || heroMeta.value.title || route.meta?.title || '')
const displayDescription = computed(() => props.description || heroMeta.value.description || '')
const displayMeta = computed(() => props.meta || heroMeta.value.meta || '')
</script>

<template>
  <header class="page-hero">
    <div class="hero-left">
      <div class="hero-meta">
        <span v-if="displayNumber" class="hero-num">{{ displayNumber }}</span>
        <span v-if="displayNumber && displaySection" class="hero-sep">/</span>
        <span v-if="displaySection" class="hero-section">{{ displaySection }}</span>
        <span v-if="displayMeta" class="hero-extra">· {{ displayMeta }}</span>
      </div>
      <h1 class="hero-title">{{ displayTitle }}</h1>
      <p v-if="displayDescription" class="hero-desc">{{ displayDescription }}</p>
    </div>
    <div class="hero-actions">
      <slot name="actions" />
    </div>
  </header>
</template>

<style scoped lang="scss">
.page-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0 10px;
  margin: 0 0 10px;
  border-bottom: 1px solid var(--rule);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    bottom: -1px;
    width: 56px;
    height: 1px;
    background: var(--accent);
  }

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 18px 0 14px;
  }
}

.hero-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.hero-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-faint);

  .hero-num {
    color: var(--accent);
    font-weight: 600;
  }

  .hero-section {
    color: var(--text-secondary);
  }

  .hero-extra {
    color: var(--text-muted);
  }

  .hero-sep {
    opacity: 0.5;
  }
}

.hero-title {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--text-primary);
}

.hero-desc {
  margin: 4px 0 0;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .page-hero {
    gap: 12px;
  }

  .hero-title {
    font-size: 20px;
  }

  .hero-desc {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .hero-actions {
    gap: 8px;

    > * {
      min-height: var(--touch-target);
    }
  }
}
</style>
